import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { MAP_CONTENT_MEMBER_LAYER_ORDER, MAP_CONTENT_PROVIDER_GROUPS, MAP_CONTENT_PROVIDER_OPTION_ORDER } from "@app/projects/network-assessment/multi-map-export/utils/mapContentOptions";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { MultiMapExportContentGroup, MultiMapExportMemberLayer, MultiMapExportProviderGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";
import Roles from "@app/user/roles";

/**
 * The map only paints terminated providers black for these roles and shows everyone else network blue
 * (NetworkCoverageMap.tsx), so rather than offer a swatch that lies, the option is not offered at all.
 * Takes roles rather than the user because UserContext's default value is null.
 */
export const canSeeTerminatedProviders = (roles?: readonly string[]): boolean =>
  !!roles?.includes(Roles.TERM_PROVIDER_ADMIN) || !!roles?.includes(Roles.TERM_PROVIDER_VIEWER);

/**
 * Gated on role alone. The map additionally requires the latest published run and at least one terminated
 * provider in the fetched set; neither is replicated, because this is a configuration surface rather than a
 * rendering of fetched data, and mirroring them would mean a fetch.
 */
export const visibleProviderGroups = (canSeeTerminated: boolean): readonly MultiMapExportProviderGroup[] =>
  canSeeTerminated
    ? MAP_CONTENT_PROVIDER_GROUPS
    : MAP_CONTENT_PROVIDER_GROUPS.map((group) => ({
      ...group,
      options: group.options.filter((option) => option.value !== ProvidersFilterOptions.Terminated),
    }));

/**
 * Derived from the group passed in, which is the *visible* group — with Terminated hidden the Network group
 * has one leaf, so checking it reads as Checked rather than a permanently indeterminate parent.
 */
export const groupCheckboxState = <TValue>(
  group: MultiMapExportContentGroup<TValue>,
  selectedValues: readonly TValue[],
): CheckboxStateEnum => {
  const selected = new Set(selectedValues);
  const selectedCount = group.options.filter((option) => selected.has(option.value)).length;

  if (selectedCount === 0) return CheckboxStateEnum.Unchecked;
  if (selectedCount === group.options.length) return CheckboxStateEnum.Checked;

  return CheckboxStateEnum.Indeterminate;
};

/** Sorts into the canonical order and deduplicates, since each value appears in that order once. */
const orderBy = <TValue>(canonicalOrder: readonly TValue[], values: readonly TValue[]): TValue[] =>
  canonicalOrder.filter((value) => values.includes(value));

export const orderProviderOptions = (options: readonly ProvidersFilterOptions[]): ProvidersFilterOptions[] =>
  orderBy(MAP_CONTENT_PROVIDER_OPTION_ORDER, options);

export const orderMemberLayers = (layers: readonly MultiMapExportMemberLayer[]): MultiMapExportMemberLayer[] =>
  orderBy(MAP_CONTENT_MEMBER_LAYER_ORDER, layers);

/** Selects or clears every leaf of the group, leaving values outside it untouched. */
export const toggleGroup = <TValue>(
  selectedValues: readonly TValue[],
  group: MultiMapExportContentGroup<TValue>,
  isSelected: boolean,
  canonicalOrder: readonly TValue[],
): TValue[] => {
  const groupValues = group.options.map((option) => option.value);
  const withoutGroup = selectedValues.filter((value) => !groupValues.includes(value));

  return orderBy(canonicalOrder, isSelected ? [...withoutGroup, ...groupValues] : withoutGroup);
};

export const toggleOption = <TValue>(
  selectedValues: readonly TValue[],
  value: TValue,
  isSelected: boolean,
  canonicalOrder: readonly TValue[],
): TValue[] => orderBy(canonicalOrder, isSelected
  ? [...selectedValues, value]
  : selectedValues.filter((selected) => selected !== value));

/**
 * Hidden means unselected. The toggle helpers already work from the visible groups, so nothing can select a
 * hidden option today; running every dispatch through this makes that structural rather than a property of
 * the current call sites, so a later section or refactor cannot smuggle one into the export payload.
 */
export const retainVisibleProviderOptions = (
  options: readonly ProvidersFilterOptions[],
  visibleGroups: readonly MultiMapExportProviderGroup[],
): ProvidersFilterOptions[] => {
  const visible = new Set(visibleGroups.flatMap((group) => group.options.map((option) => option.value)));

  return options.filter((option) => visible.has(option));
};
